import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { useAuth } from './context/AuthContext';
import FETExperience from './pages/FETExperience.jsx';
import FETDashboard from './pages/FETDashboard.jsx';
import Login from './pages/Login.jsx';

// Heavy routes that the average student never visits — keep them out of
// the initial bundle so the first paint of /, /dashboard, and /exam/:id
// doesn't have to download Admin's huge JSON editors or the Reports
// charts. Each becomes a separate chunk loaded on demand.
const Admin = lazy(() => import('./pages/Admin.jsx'));
const Reports = lazy(() => import('./pages/Reports.jsx'));
const Pricing = lazy(() => import('./pages/Pricing.jsx'));
const CreditHistory = lazy(() => import('./pages/CreditHistory.jsx'));

// Minimal placeholder while a chunk streams in. Intentionally tiny — we
// don't want a full skeleton flashing for what's usually a sub-200ms
// load on a warm cache.
function RouteFallback() {
  return null;
}

function AdminRoute() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return <Admin />;
}

function ReportsRoute() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/reports" replace />;
  return <Reports />;
}

function PricingRoute() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/pricing" replace />;
  return <Pricing />;
}

function CreditHistoryRoute() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/credit-history" replace />;
  return <CreditHistory />;
}

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function App() {
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<FETExperience />} />
                <Route path="/dashboard" element={<FETDashboard />} />
                <Route path="/exam/:examId" element={<FETExperience />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<AdminRoute />} />
                <Route path="/reports" element={<ReportsRoute />} />
                <Route path="/pricing" element={<PricingRoute />} />
                <Route path="/credit-history" element={<CreditHistoryRoute />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
